import React, { useState, useEffect } from 'react';
import { Skull, AlertCircle, Plus, Camera, Paperclip, Check, ShieldAlert } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Combobox } from '@/components/common/Combobox';
import { mortalityService } from '@/services/mortalityService';
import { cycleService } from '@/services/cycleService';
import { ProductionCycleRecord, ProductionUnitRecord } from '@/types/database';
import { useAuth } from '@/context/AuthContext';

interface MortalityLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (event: any) => void;
  cycleId?: string | null;
  cycleCode?: string;
  currentLiveHeadcount?: number;
}

export const MortalityLogModal: React.FC<MortalityLogModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  cycleId,
  cycleCode,
  currentLiveHeadcount,
}) => {
  const { isAdmin } = useAuth();

  const [selectedCycleId, setSelectedCycleId] = useState<string>(cycleId || '');
  const [cycles, setCycles] = useState<ProductionCycleRecord[]>([]);
  const [productionUnits, setProductionUnits] = useState<ProductionUnitRecord[]>([]);

  const [eventDate, setEventDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [headCount, setHeadCount] = useState<number | ''>(1);
  const [totalWeightKg, setTotalWeightKg] = useState<number | ''>('');
  const [causeOfDeath, setCauseOfDeath] = useState<string>('');
  const [disposalMethod, setDisposalMethod] = useState<string>('');
  const [productionUnitId, setProductionUnitId] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [attachedFile, setAttachedFile] = useState<File | null>(null);

  // Admin Override state
  const [adminOverrideReason, setAdminOverrideReason] = useState<string>('');
  const [confirmAdminOverride, setConfirmAdminOverride] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const suggestedCauses = mortalityService.getSuggestedCauses();
  const suggestedDisposal = mortalityService.getSuggestedDisposalMethods();

  useEffect(() => {
    if (cycleId) {
      setSelectedCycleId(cycleId);
    }
  }, [cycleId]);

  useEffect(() => {
    if (!isOpen) return;
    setErrorMsg(null);
    setAdminOverrideReason('');
    setConfirmAdminOverride(false);

    if (!cycleId) {
      cycleService.getCycles({ status: 'active' }).then((c) => {
        setCycles(c);
        if (c.length > 0 && !selectedCycleId) {
          setSelectedCycleId(c[0].id);
        }
      });
    }

    cycleService.getProductionUnits().then((units) => {
      setProductionUnits(units);
    });
  }, [isOpen, cycleId]);

  const targetHead = typeof currentLiveHeadcount === 'number' ? currentLiveHeadcount : undefined;
  const isExceedingLiveHead = targetHead !== undefined && Number(headCount) > targetHead;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setAttachedFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    const targetCycleId = cycleId || selectedCycleId;
    if (!targetCycleId) {
      setErrorMsg('Please select a Production Cycle.');
      return;
    }
    if (!eventDate) {
      setErrorMsg('Event Date is required.');
      return;
    }
    if (!headCount || Number(headCount) <= 0) {
      setErrorMsg('Head Count lost must be greater than zero.');
      return;
    }

    // Enforce impossible-headcount rule
    if (isExceedingLiveHead) {
      if (!isAdmin) {
        setErrorMsg(
          `Impossible Headcount Balance: Mortality count (${headCount}) exceeds current live balance (${targetHead} head). Operations users cannot post negative live balances. Please contact an Administrator to review or override.`
        );
        return;
      }
      if (!confirmAdminOverride || !adminOverrideReason.trim()) {
        setErrorMsg('Admin override requires a mandatory justification reason.');
        return;
      }
    }

    setLoading(true);
    try {
      let finalNotes = notes.trim();
      if (isExceedingLiveHead && isAdmin) {
        finalNotes = `[Admin Override: ${adminOverrideReason.trim()}] ${finalNotes}`.trim();
      }

      const created = await mortalityService.logMortality(
        {
          cycle_id: targetCycleId,
          event_date: eventDate,
          head_count: Number(headCount),
          total_weight_kg: totalWeightKg !== '' ? Number(totalWeightKg) : null,
          cause_of_death: causeOfDeath.trim() || null,
          disposal_method: disposalMethod.trim() || null,
          production_unit_id: productionUnitId || null,
          notes: finalNotes || null,
        },
        attachedFile
      );

      onSuccess(created);
      onClose();
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to log mortality event.');
    } finally {
      setLoading(false);
    }
  };

  const cycleOptions = cycles.map((c) => ({
    value: c.id,
    label: `${c.cycle_code} — ${c.growers?.full_name || 'Company Owned'}`,
  }));

  const unitOptions = productionUnits.map((u) => ({
    value: u.id,
    label: `${u.unit_name}${u.unit_code ? ` (${u.unit_code})` : ''}`,
  }));

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader className="border-b border-slate-200 pb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-red-100 text-red-700 flex items-center justify-center font-bold">
              <Skull className="w-4 h-4" />
            </div>
            <div>
              <DialogTitle className="text-base sm:text-lg font-bold text-slate-900">
                Log Swine Mortality Event
              </DialogTitle>
              <p className="text-xs text-slate-500">
                Record animal loss with flexible cause classification, disposal method, and carcass photo.
              </p>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          {errorMsg && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Cycle Target Banner or Select */}
          {cycleId ? (
            <div className="p-3 bg-red-50/60 rounded-lg border border-red-200 text-xs text-slate-800 flex items-center justify-between">
              <div>
                Target Cycle: <strong className="font-mono text-red-900">{cycleCode || cycleId}</strong>
              </div>
              {targetHead !== undefined && (
                <div className="text-slate-600 font-mono">
                  Live on feed: <strong>{targetHead} head</strong>
                </div>
              )}
            </div>
          ) : (
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Target Production Cycle *</label>
              <Combobox
                options={cycleOptions}
                value={selectedCycleId}
                onChange={setSelectedCycleId}
                placeholder="Select Target Production Cycle..."
                searchPlaceholder="Search active cycles..."
              />
            </div>
          )}

          {/* Event Date & Head Count */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Mortality Date *</label>
              <Input
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                className="h-10 text-xs font-mono"
                required
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-800 block mb-1">Head Count Lost *</label>
              <Input
                type="number"
                min="1"
                step="1"
                tabular
                inputMode="numeric"
                value={headCount}
                onChange={(e) => setHeadCount(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                placeholder="1"
                className="h-10 text-xs font-mono font-bold"
                required
              />
            </div>
          </div>

          {/* Impossible-Negative Headcount Guard */}
          {isExceedingLiveHead && (
            <div className="p-3.5 rounded-lg bg-red-50 border border-red-300 text-red-900 text-xs space-y-2.5">
              <div className="flex items-start gap-2 font-bold text-red-800">
                <ShieldAlert className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <span>
                  Impossible Balance: Head count ({headCount}) exceeds current live balance ({targetHead} head).
                </span>
              </div>

              {!isAdmin ? (
                <p className="text-red-700 leading-snug">
                  Operations role cannot post negative live headcount transactions. Please check your count or contact an Administrator to review.
                </p>
              ) : (
                <div className="space-y-2 pt-1 border-t border-red-200">
                  <p className="text-slate-800 font-semibold">
                    Administrator Override Mode: Mandatory justification required.
                  </p>
                  <div>
                    <label className="text-[11px] font-bold text-slate-700 block mb-1">
                      Mandatory Override Reason *
                    </label>
                    <Input
                      type="text"
                      value={adminOverrideReason}
                      onChange={(e) => setAdminOverrideReason(e.target.value)}
                      placeholder="e.g. Historical count discrepancy, physical verification confirmed..."
                      className="h-10 text-xs bg-white"
                      required
                    />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer font-medium text-slate-900 pt-1 min-h-[44px]">
                    <input
                      type="checkbox"
                      checked={confirmAdminOverride}
                      onChange={(e) => setConfirmAdminOverride(e.target.checked)}
                      className="rounded text-red-600 focus:ring-red-500 w-4 h-4"
                    />
                    <span>I confirm this Administrative Override and accept the audit record</span>
                  </label>
                </div>
              )}
            </div>
          )}

          {/* Total Weight & Pen */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                Total Weight (kg) <span className="text-slate-400 font-normal">(Optional)</span>
              </label>
              <Input
                type="number"
                step="0.1"
                min="0"
                tabular
                inputMode="decimal"
                value={totalWeightKg}
                onChange={(e) => setTotalWeightKg(e.target.value === '' ? '' : parseFloat(e.target.value))}
                placeholder="e.g. 45.0"
                className="h-10 text-xs font-mono"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                Production Unit / Pen <span className="text-slate-400 font-normal">(Optional)</span>
              </label>
              <Combobox
                options={unitOptions}
                value={productionUnitId}
                onChange={setProductionUnitId}
                allowAll={true}
                allLabel="Select Pen / Barn..."
                placeholder="Select Pen / Barn..."
                searchPlaceholder="Search pen..."
              />
            </div>
          </div>

          {/* Flexible Cause of Death with Suggestions */}
          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">Cause of Death</label>
            <div className="space-y-1.5">
              <Input
                type="text"
                list="causes-list"
                value={causeOfDeath}
                onChange={(e) => setCauseOfDeath(e.target.value)}
                placeholder="Type or select cause (e.g. Respiratory, Heat Stress, Crushing...)"
                className="h-10 text-xs"
              />
              <datalist id="causes-list">
                {suggestedCauses.map((cause) => (
                  <option key={cause} value={cause} />
                ))}
              </datalist>
            </div>
          </div>

          {/* Flexible Disposal Method with Suggestions */}
          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">Disposal Method</label>
            <div className="space-y-1.5">
              <Input
                type="text"
                list="disposal-list"
                value={disposalMethod}
                onChange={(e) => setDisposalMethod(e.target.value)}
                placeholder="Type or select disposal method (e.g. Deep Burial with Lime, Composting...)"
                className="h-10 text-xs"
              />
              <datalist id="disposal-list">
                {suggestedDisposal.map((method) => (
                  <option key={method} value={method} />
                ))}
              </datalist>
            </div>
          </div>

          {/* Carcass / Disposal Photo Upload */}
          <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50 space-y-2">
            <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
              <Camera className="w-3.5 h-3.5 text-red-700" /> Carcass / Disposal Photo Attachment
            </label>
            <div className="flex items-center gap-3">
              <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-xs font-medium text-slate-700 transition-colors shadow-xs min-h-[44px]">
                <Paperclip className="w-3.5 h-3.5 text-slate-500" />
                {attachedFile ? attachedFile.name : 'Take photo or choose file'}
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </label>
              {attachedFile && (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-medium">
                  <Check className="w-3.5 h-3.5" /> Ready to upload
                </span>
              )}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">Notes</label>
            <Input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Clinical symptoms, treatment attempts, biosecurity actions taken..."
              className="h-10 text-xs"
            />
          </div>

          <DialogFooter className="sticky bottom-0 bg-white border-t border-slate-200 pt-3 flex flex-row items-center justify-end gap-2 z-10">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={loading} className="min-h-[44px]">
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white font-semibold gap-1 min-h-[44px]"
              disabled={loading || (isExceedingLiveHead && (!isAdmin || !confirmAdminOverride || !adminOverrideReason.trim()))}
            >
              <Skull className="w-4 h-4" /> {loading ? 'Logging...' : 'Log Mortality'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
